import { useEffect, useState } from 'react';
import { emergencySos } from '../lib/api';
import { Siren, PhoneCall, CheckCircle2, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Pharmacy-owner side of the Emergency SOS engine. Lists open broadcasts
 * targeting the owner's pharmacy so they can respond with a single tap.
 */
export default function OwnerEmergencyAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    emergencySos.ownerAlerts('open')
      .then((d) => setAlerts(d.broadcasts || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const respond = async (b, has_it) => {
    setBusyId(b.id);
    try {
      await emergencySos.respond(b.id, { has_it, note: has_it ? 'Available — call the shop' : 'Currently out of stock' });
      toast.success(has_it ? 'Customer notified — they can call you now.' : 'Marked as unavailable');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not respond');
    } finally { setBusyId(null); }
  };

  if (loading) {
    return <div className="text-ink/50 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading emergencies…</div>;
  }

  return (
    <div data-testid="owner-emergency-alerts">
      <div className="flex items-center gap-2 mb-3">
        <Siren className="w-4 h-4 text-[#B71C1C]"/>
        <div className="font-heading font-black text-lg">Open emergency SOS ({alerts.length})</div>
      </div>
      <p className="text-xs text-ink/60 mb-4">
        Customers within 5 km sent these SOS requests. Tap <b>I have it</b> and the customer will get a call button to reach you directly.
      </p>

      {alerts.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-ink/20 p-8 text-center text-sm text-ink/60">
          No open emergencies right now.
        </div>
      ) : (
        <ul className="space-y-3">
          {alerts.map((b) => {
            const mine = b.my_response;
            return (
              <li
                key={b.id}
                data-testid={`owner-emergency-${b.id}`}
                className="rounded-2xl border-2 border-[#B71C1C]/30 bg-[#FFF7F7] p-4"
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="w-10 h-10 rounded-lg bg-[#B71C1C] text-white grid place-items-center shrink-0">
                    <Siren className="w-5 h-5"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#B71C1C] text-white px-2 py-0.5 text-[10px] font-black uppercase tracking-wider">
                        {b.urgency || 'high'}
                      </span>
                      {b.requires_prescription && (
                        <span className="inline-flex items-center rounded-full bg-white border border-[#B71C1C] text-[#B71C1C] px-2 py-0.5 text-[10px] font-black uppercase tracking-wider">
                          Rx-only
                        </span>
                      )}
                      <span className="text-[10px] uppercase tracking-[0.16em] font-black text-ink/50">
                        {new Date(b.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="font-heading font-black text-lg mt-1 text-ink">{b.medicine_name}</div>
                    {b.notes && <div className="text-xs text-ink/70 mt-0.5 italic">&ldquo;{b.notes}&rdquo;</div>}
                    <div className="text-[11px] text-ink/60 mt-1 inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3"/>
                      Customer: {b.customer_name || 'anonymous'} · SOS to {(b.targets || []).length} pharmacies
                    </div>
                    {b.prescription_url && (
                      <a
                        href={b.prescription_url}
                        target="_blank" rel="noreferrer"
                        className="mt-2 inline-block text-[11px] font-black uppercase tracking-wider text-[#0D47A1] hover:underline"
                      >
                        View prescription →
                      </a>
                    )}
                  </div>
                </div>

                {mine ? (
                  <div className="mt-3 rounded-lg bg-white border border-ink/10 p-3 text-sm">
                    <span className="inline-flex items-center gap-1 font-black text-[#1B5E20]">
                      <CheckCircle2 className="w-4 h-4"/>Responded: {mine.has_it ? 'Have it in stock' : 'Not available'}
                    </span>
                  </div>
                ) : (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button
                      data-testid={`owner-emergency-have-${b.id}`}
                      onClick={() => respond(b, true)}
                      disabled={busyId === b.id}
                      className="inline-flex items-center gap-1.5 rounded-md bg-[#1B5E20] hover:bg-[#0D3F12] disabled:opacity-50 text-white font-black uppercase text-xs tracking-wider px-4 h-10"
                    >
                      <PhoneCall className="w-3.5 h-3.5"/>I have it — call me
                    </button>
                    <button
                      data-testid={`owner-emergency-not-${b.id}`}
                      onClick={() => respond(b, false)}
                      disabled={busyId === b.id}
                      className="inline-flex items-center gap-1.5 rounded-md border-2 border-ink/20 hover:border-ink text-ink font-black uppercase text-xs tracking-wider px-4 h-10"
                    >
                      Not available
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
